/**
 * Lecturer Search and Filters Helper Functions
 *
 * This module provides filtering functionality for lecturers based on their associated educations.
 * Lecturers are filtered by evaluating their educations against search terms, filters, and tags.
 */

import { filterEducations, filterEducationsByTag } from './education-filters.helpers.js';

/**
 * Extracts unique educations taught by a lecturer
 *
 * A lecturer's educations are accessed through their modules. Each module can belong to:
 * - A standalone education (webinar) via module.standaloneEducation
 * - One or more multi-module educations (requires matching against educations array)
 *
 * @param {Object} lecturer - Lecturer object with modules join field
 * @param {Array} educations - Array of all education objects (needed for reverse lookup)
 * @returns {Array} Array of unique education objects
 */
const getRelationId = (value) => {
  if (!value) return null;

  return typeof value === 'object' ? value.id : value;
};

const normalizeItems = (value) => {
  if (!value) return [];

  if (Array.isArray(value)) return value;

  if (Array.isArray(value.docs)) return value.docs;

  return [];
};

const isLecturerMatch = (candidate, lecturer) => {
  if (!candidate || !lecturer) return false;

  const candidateId = getRelationId(candidate);
  const lecturerId = getRelationId(lecturer);

  const candidateValue = typeof candidate === 'string' ? candidate.toLowerCase() : null;

  const lecturerSlug = lecturer.slug?.toLowerCase();
  const lecturerFullName = lecturer.fullName?.toLowerCase();
  const lecturerName = `${lecturer.firstName || ''} ${lecturer.lastName || ''}`
    .trim()
    .toLowerCase();

  const candidateFullName =
    typeof candidate === 'object'
      ? candidate.fullName?.toLowerCase() ||
        `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim().toLowerCase()
      : null;

  if (candidateId !== null && lecturerId !== null && String(candidateId) === String(lecturerId)) {
    return true;
  }

  if (candidateValue && lecturerSlug && candidateValue === lecturerSlug) {
    return true;
  }

  if (candidateValue && lecturerFullName && candidateValue === lecturerFullName) {
    return true;
  }

  if (candidateValue && lecturerName && candidateValue === lecturerName) {
    return true;
  }

  if (
    typeof candidate === 'object' &&
    lecturerSlug &&
    candidate.slug?.toLowerCase() === lecturerSlug
  ) {
    return true;
  }

  if (candidateFullName && lecturerFullName && candidateFullName === lecturerFullName) {
    return true;
  }

  if (candidateFullName && lecturerName && candidateFullName === lecturerName) {
    return true;
  }

  return false;
};

const educationHasLecturer = (education, lecturer) => {
  const educationModules = normalizeItems(education?.modules?.modules);

  return educationModules.some((educationModule) => {
    const lecturerEntries = normalizeItems(educationModule?.lecturers);

    if (lecturerEntries.some((entry) => isLecturerMatch(entry?.lecturer, lecturer))) {
      return true;
    }

    return lecturerEntries.some((entry) => {
      const bioId = getRelationId(entry?.bio);

      if (bioId === null) {
        return false;
      }

      return normalizeItems(lecturer.biographies).some(
        (biography) => String(getRelationId(biography)) === String(bioId)
      );
    });
  });
};

export const getLecturerEducations = (lecturer, educations = []) => {
  // Handle null/undefined lecturer or missing modules
  if (!lecturer) {
    return [];
  }

  // Handle null/undefined educations array
  if (!educations || !Array.isArray(educations)) {
    return [];
  }

  const educationMap = new Map();

  const lecturerModules = normalizeItems(lecturer.modules);

  // Iterate through lecturer's modules
  for (const moduleData of lecturerModules) {
    if (!moduleData) continue;

    // Case 1: Standalone education (webinar)
    if (moduleData.standaloneEducation) {
      const education = moduleData.standaloneEducation;
      // Handle both populated object and ID reference
      const educationId = typeof education === 'object' ? education.id : education;

      if (educationId && !educationMap.has(educationId)) {
        // If it's already an object, use it; otherwise find it in educations array
        if (typeof education === 'object') {
          educationMap.set(educationId, education);
        } else {
          const foundEducation = educations.find((e) => e.id === educationId);
          if (foundEducation) {
            educationMap.set(educationId, foundEducation);
          }
        }
      }
    }

    // Case 2: Multi-module education (find educations that contain this module)
    const moduleId = getRelationId(moduleData);

    for (const education of educations) {
      // Skip if already added
      if (educationMap.has(education.id)) continue;

      // Check if this education contains the module
      const educationModules = normalizeItems(education.modules?.modules);
      const hasModule = educationModules.some((eduModule) => {
        const eduModuleId = typeof eduModule === 'object' ? eduModule.id : eduModule;
        return eduModuleId === moduleId;
      });

      if (hasModule) {
        educationMap.set(education.id, education);
      }
    }
  }

  if (educationMap.size === 0) {
    for (const education of educations) {
      if (!education || educationMap.has(education.id)) continue;

      if (educationHasLecturer(education, lecturer)) {
        educationMap.set(education.id, education);
      }
    }
  }

  return Array.from(educationMap.values());
};

/**
 * Filters lecturers based on their associated educations
 *
 * A lecturer is included in results if at least one of their educations matches
 * all active filters and the active tag. This function reuses the existing
 * education filtering logic from education-filters.helpers.js.
 *
 * @param {Array} lecturers - Array of lecturer objects with modules join field
 * @param {Array} educations - Array of all education objects (needed for reverse lookup)
 * @param {Array} filters - Active filter objects (search, duration, venue, upcoming)
 * @param {string|null} activeTag - Active tag slug for tag-based filtering
 * @returns {Array} Array of lecturers with at least one matching education
 */
export const filterLecturersByEducations = (
  lecturers,
  educations,
  filters = [],
  activeTag = null
) => {
  // Handle null/undefined lecturers
  if (!lecturers || !Array.isArray(lecturers)) {
    return [];
  }

  // Handle null/undefined educations
  if (!educations || !Array.isArray(educations)) {
    return [];
  }

  const lecturerMap = new Map();
  const hasActiveFilters = (filters && filters.length > 0) || activeTag;

  // Iterate through each lecturer
  for (const lecturer of lecturers) {
    if (!lecturer) continue;

    // Extract educations for this lecturer
    const lecturerEducations = getLecturerEducations(lecturer, educations);

    // If lecturer has no educations, skip them
    if (lecturerEducations.length === 0) continue;

    // If no filters are active, include all lecturers with educations
    if (!hasActiveFilters) {
      if (!lecturerMap.has(lecturer.id)) {
        lecturerMap.set(lecturer.id, lecturer);
      }
      continue;
    }

    // Apply education filters
    let filteredEducations = filterEducations(lecturerEducations, filters);

    // Apply tag filtering
    filteredEducations = filterEducationsByTag(filteredEducations, activeTag);

    // If at least one education matches, include the lecturer
    if (filteredEducations.length > 0) {
      // Deduplicate by lecturer ID
      if (!lecturerMap.has(lecturer.id)) {
        lecturerMap.set(lecturer.id, lecturer);
      }
    }
  }

  return Array.from(lecturerMap.values());
};

export const filterLecturersFromMatchingEducations = (
  lecturers,
  educations,
  filters = [],
  activeTag = null
) => {
  if (!lecturers || !Array.isArray(lecturers)) {
    return [];
  }

  if (!educations || !Array.isArray(educations)) {
    return [];
  }

  const filteredEducations = filterEducationsByTag(
    filterEducations(educations, filters),
    activeTag
  );
  const matchedLecturers = new Map();

  for (const education of filteredEducations) {
    const educationModules = normalizeItems(education?.modules?.modules);

    for (const educationModule of educationModules) {
      const educationLecturerEntries = normalizeItems(educationModule?.lecturers);

      for (const entry of educationLecturerEntries) {
        const matchingLecturer = lecturers.find((lecturer) =>
          isLecturerMatch(entry?.lecturer, lecturer)
        );

        if (matchingLecturer && !matchedLecturers.has(matchingLecturer.id)) {
          matchedLecturers.set(matchingLecturer.id, matchingLecturer);
        }

        const bioId = getRelationId(entry?.bio);

        if (bioId === null) {
          continue;
        }

        const biographyMatchedLecturer = lecturers.find((lecturer) =>
          normalizeItems(lecturer.biographies).some(
            (biography) => String(getRelationId(biography)) === String(bioId)
          )
        );

        if (biographyMatchedLecturer && !matchedLecturers.has(biographyMatchedLecturer.id)) {
          matchedLecturers.set(biographyMatchedLecturer.id, biographyMatchedLecturer);
        }
      }
    }
  }

  return Array.from(matchedLecturers.values());
};

/**
 * Filters lecturers by name and associated education titles
 *
 * Matches search-type filters against:
 * - Lecturer firstName and lastName
 * - Titles of educations the lecturer teaches
 *
 * @param {Array} lecturers - Array of lecturer objects
 * @param {Array} educations - Array of all education objects (for reverse lookup)
 * @param {Array} filters - Active filter objects (only 'search' type is handled)
 * @returns {Array} Filtered array of lecturers matching all search terms
 */
export const filterLecturers = (lecturers, educations, filters = []) => {
  if (!lecturers || !Array.isArray(lecturers)) return [];
  if (!filters || filters.length === 0) return lecturers;

  const searchFilters = filters.filter((f) => f.type === 'search');
  if (searchFilters.length === 0) return lecturers;

  return lecturers.filter((lecturer) => {
    const firstName = (lecturer.firstName || '').toLowerCase();
    const lastName = (lecturer.lastName || '').toLowerCase();
    const fullName = `${firstName} ${lastName}`;

    const lecturerEducations = getLecturerEducations(lecturer, educations);
    const educationTitles = lecturerEducations.map((e) => (e.title || '').toLowerCase()).join(' ');

    return searchFilters.every((filter) => {
      const term = filter.value.toLowerCase();
      return fullName.includes(term) || educationTitles.includes(term);
    });
  });
};
